/* eslint-disable no-undef */
/* eslint-disable no-console */

import { proxyAuthState as proxyState } from "../../service/userAuthApi";
import jwtDecode from "jwt-decode";
import { NUMBER } from "../../constants/app-constants";
import { saveBifrostAuthCookies } from "./auth-cookie";
import bifrostService from "../../service/bifrost-service";
import bifrostAuthService from "../../service/bifrost-auth-service";
import parseCookie from "./parse-cookie";
import isBifrostToken, { isOldAuToken } from "./is-bifrost-token";

const DEBUG = process.env.NODE_ENV === "development";

const WAIT_TIME_IN_SECS = NUMBER.SIXTY;

// Request Interceptor which handles token refresh pre and post token expiry
// eslint-disable-next-line max-statements, complexity
const authRefresh = async (config, ...args) => {
    const proxyAuthState = args[1] || args[0] || proxyState;
    if (config.headers.Authorization) {
        const authToken = (config.headers.Authorization && config.headers.Authorization.split(" ")[1]) || null;
        let authTokenDecoded = null;
        try {
            authTokenDecoded = authToken ? jwtDecode(authToken) : null;
        } catch (e) {
            // Not a decodable JWT (e.g. opaque/guest token) — skip refresh, let the
            // request proceed as-is rather than throwing an unhandled rejection.
            return config;
        }
        const currentTimeInSec = new Date().getTime() / NUMBER.THOUSAND;
        // Bifrost (UMS) tokens have no legacy refresh path — attempting it always
        // fails and flips isLoggedIn → false, which can re-trigger login in a loop.
        // update access token when expiry time is less than 60 secs
        if (authToken && authTokenDecoded && isBifrostToken(authToken) && authTokenDecoded.exp - currentTimeInSec < WAIT_TIME_IN_SECS) {
            if (typeof window === "undefined") return config; // SSR — skip client-only bifrost refresh
            try {
                const authResponse = await bifrostService.refreshToken({
                    refreshToken: parseCookie("bifrost_refresh_token"),
                    sessionId: parseCookie("bifrost_session_id")
                });
                proxyAuthState.accessToken = authResponse.token;
                proxyAuthState.expiresIn = authResponse.expires_in;
                proxyAuthState.isLoggedIn = true;
                config.headers.Authorization = `Bearer ${authResponse.token}`;
            } catch (err) {
                proxyAuthState.isLoggedIn = false;
            }
        } else if (authToken && authTokenDecoded && isOldAuToken(authToken)) {
            // Old AU token detected — exchange for Bifrost token
            if (typeof window === "undefined") return config;
            try {
                const exchangeResponse = await bifrostAuthService.exchangeOldAuToken(authToken);
                const newToken = exchangeResponse?.data?.access_token || exchangeResponse?.access_token;
                if (newToken) {
                    proxyAuthState.accessToken = newToken;
                    proxyAuthState.isLoggedIn = true;
                    config.headers.Authorization = `Bearer ${newToken}`;
                    saveBifrostAuthCookies({
                        accessToken: newToken,
                        refreshToken: exchangeResponse?.data?.refresh_token || exchangeResponse?.refresh_token,
                        sessionId: exchangeResponse?.data?.session_id || exchangeResponse?.session_id,
                        expiresIn: exchangeResponse?.data?.expires_in || exchangeResponse?.expires_in
                    });
                }
            } catch (err) {
                // Let request proceed with old token
            }
        }
        if (proxyAuthState.isLoggedIn) {
            saveBifrostAuthCookies({
                accessToken: proxyAuthState.accessToken,
                expiresIn: proxyAuthState.expiresIn
            });
        }
    }
    if (DEBUG) {
        console.debug("✉️ ", config);
    }
    return config;
};

export default authRefresh;
